import { useState } from "react";
import { ChevronDown, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNavigate } from "react-router-dom";
import { useColonies } from "@/hooks/useColonies";
import { cn } from "@/lib/utils";

interface ColonySelectorProps {
  currentColonyId?: string;
  variant?: "button" | "minimal";
  onColonySelect?: (colonyId: string) => void;
}

export function ColonySelector({
  currentColonyId,
  variant = "button",
  onColonySelect
}: ColonySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { colonies, isLoading } = useColonies();

  const currentColony = colonies.find(c => c.id === currentColonyId);

  const handleColonySelect = (colonyId: string) => {
    setIsOpen(false);
    if (onColonySelect) {
      onColonySelect(colonyId);
    } else {
      navigate(`/chat/${colonyId}`);
    }
  };

  const handleCreateNew = () => {
    setIsOpen(false);
    navigate("/add-colony");
  };

  const Content = () => (
    <div className="w-[min(92vw,22rem)] p-2 bg-[#121212] border border-white/10 rounded-xl shadow-2xl">
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <div className="w-5 h-5 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
        </div>
      ) : colonies.length === 0 ? (
        <div className="py-4">
          <div className="text-center mb-4">
            <div className="ios-subheadline text-muted-foreground mb-1">No colonies yet</div>
            <div className="ios-caption-2 text-muted-foreground">Create your first colony to get started</div>
          </div>
          <Button
            onClick={handleCreateNew}
            className="w-full ios-button bg-ios-blue hover:bg-ios-blue/90 text-white border-0"
          >
            <Plus size={16} className="mr-2" />
            Create Your First Colony
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="px-3 py-2 text-xs uppercase tracking-wide text-white/60">Your Colonies</div>
          {colonies.map((colony) => (
            <Button
              key={colony.id}
              variant="ghost"
              className={cn(
                "w-full justify-between h-auto p-3 hover:bg-white/5 rounded-lg",
                currentColonyId === colony.id && "bg-ios-surface-secondary"
              )}
              onClick={() => handleColonySelect(colony.id)}
            >
              <div className="text-left flex-1">
                <div className="ios-callout font-medium text-foreground">{colony.name}</div>
                {colony.description && (
                  <div className="text-xs text-white/60 mt-1 whitespace-normal break-words line-clamp-2">
                    {colony.description}
                  </div>
                )}
              </div>
              <ArrowRight size={16} className="text-muted-foreground ml-2" />
            </Button>
          ))}
          <div className="border-t border-white/10 my-2" />
          <Button
            onClick={handleCreateNew}
            variant="ghost"
            className="w-full justify-start p-3 hover:bg-white/5 rounded-lg text-white"
          >
            <Plus size={16} className="mr-3" />
            <span className="ios-callout font-medium">New Colony</span>
          </Button>
        </div>
      )}
    </div>
  );

  if (variant === "minimal") {
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 px-2 text-white hover:bg-white/10">
            <span className="ios-callout">{currentColony ? currentColony.name : "Select Colony"}</span>
            <ChevronDown size={16} className="ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="p-0 border-0 bg-transparent shadow-none">
          <Content />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="justify-between bg-ios-surface border-ios-separator hover:bg-ios-surface-secondary ios-callout"
        >
          <span>{currentColony ? currentColony.name : "Jump to Colony"}</span>
          <ChevronDown size={16} className="ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 border-0 bg-transparent shadow-none">
        <Content />
      </PopoverContent>
    </Popover>
  );
}


